import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import {
  Send,
  Info,
  Bot,
  User as UserIcon,
  Eye,
  MessageSquare,
  Bell,
  Zap,
  FileText,
  X,
  ChevronDown,
  ChevronUp,
  Copy,
  EyeOff,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { Event, Session, User, Notification, AppUser, ExternalApiKeyView, ChatExternalCallResult } from '../../types';
import {
  eventsApi, chatApi, sessionsApi, apiService, notificationsApi, adminApi,
  externalApiKeyApi, callChatExternal, buildChatExternalCurl,
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Prism } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Type assertion to fix TypeScript compatibility issue with react-syntax-highlighter
// This is a known issue with react-syntax-highlighter types and React 18+
const SyntaxHighlighter = Prism as any as React.ComponentType<any>;

interface ChatMessage {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  metadata?: any;
}

// Simple collapsible panel component
const CollapsePanel: React.FC<{
  header: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}> = ({ header, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-md mb-2">
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left text-sm font-medium text-gray-700 rounded-t-md"
        onClick={() => setOpen(o => !o)}
      >
        <span>{header}</span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && <div className="px-4 py-3">{children}</div>}
    </div>
  );
};

// Simple tab component
const TabGroup: React.FC<{
  tabs: { key: string; label: string; content: React.ReactNode }[];
  defaultTab?: string;
}> = ({ tabs, defaultTab }) => {
  const [active, setActive] = useState(defaultTab || tabs[0]?.key);
  return (
    <div>
      <div className="flex border-b border-gray-200 mb-4 gap-1">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActive(tab.key)}
            className={`px-4 py-2 text-sm font-medium rounded-t-md border border-b-0 ${
              active === tab.key
                ? 'bg-white text-blue-600 border-gray-200 -mb-px'
                : 'bg-gray-50 text-gray-600 border-transparent hover:text-gray-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {tabs.find(t => t.key === active)?.content}
    </div>
  );
};

// Simple modal component
const Modal: React.FC<{
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  wide?: boolean;
}> = ({ open, onClose, title, children, wide }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-5 px-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className={`relative bg-white rounded-lg shadow-xl w-full ${wide ? 'max-w-5xl' : 'max-w-lg'} max-h-[90vh] flex flex-col`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 font-semibold text-gray-900">{title}</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">{children}</div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Small helpers used by the External API tab
const apiErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;
const formatUsd = (value: number): string => `$${value < 1 ? value.toFixed(4) : value.toFixed(2)}`;

// =====================================================================================
// "External API" tab: calls the REAL POST /api/chat/external exactly as a third-party
// integrator would (X-API-Key header, no JWT). Admin only. The key lives in component state
// only — never localStorage/sessionStorage, never logged.
// =====================================================================================

interface ExtChatLine {
  id: string;
  role: 'user' | 'bot' | 'error';
  text: string;
}

interface ScenarioResult {
  id: string;
  name: string;
  expected: string;
  actual: string;
  passed: boolean;
  note?: string;
}

const randomHex = (length: number): string => {
  const bytes = new Uint8Array(Math.ceil(length / 2));
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('').slice(0, length);
};

const randomSecret = (length = 43): string => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => alphabet[b % alphabet.length]).join('');
};

const errorCodeOf = (result: ChatExternalCallResult): string =>
  result.networkError ? 'NETWORK_ERROR' : (result.body?.error?.code as string | undefined) || (result.status === 200 ? 'OK' : `HTTP_${result.status}`);

const ExternalApiTester: React.FC = () => {
  const [clients, setClients] = useState<AppUser[]>([]);
  const [clientId, setClientId] = useState('');
  const [keys, setKeys] = useState<ExternalApiKeyView[]>([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [keyId, setKeyId] = useState('');
  const [keySource, setKeySource] = useState<'existing' | 'manual'>('existing');
  const [manualKey, setManualKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [activeKey, setActiveKey] = useState(''); // loaded via audited reveal; component state only
  const [loadingKey, setLoadingKey] = useState(false);

  const [events, setEvents] = useState<Event[]>([]);
  const [eventChoice, setEventChoice] = useState('');
  const [customEventId, setCustomEventId] = useState('');

  const [message, setMessage] = useState('');
  const [continueSession, setContinueSession] = useState(true);
  const [sessionId, setSessionId] = useState('');
  const [lines, setLines] = useState<ExtChatLine[]>([]);
  const [sending, setSending] = useState(false);

  const [lastCall, setLastCall] = useState<{ result: ChatExternalCallResult; curl: string } | null>(null);
  const [scenarioResults, setScenarioResults] = useState<ScenarioResult[]>([]);
  const [runningScenario, setRunningScenario] = useState('');
  const [verify, setVerify] = useState<null | { loading: boolean; checks: { label: string; ok: boolean; detail?: string }[]; error?: string }>(null);
  const chatRef = useRef<HTMLDivElement>(null);

  const selectedKey = keys.find(k => k._id === keyId);
  const effectiveKey = keySource === 'manual' ? manualKey.trim() : activeKey;
  const eventId = (eventChoice === '__custom__' ? customEventId : eventChoice).trim();

  useEffect(() => {
    adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true })
      .then(res => setClients(res.data.data?.users || []))
      .catch(() => toast.error('Failed to load clients'));
  }, []);

  useEffect(() => {
    setKeys([]); setKeyId(''); setActiveKey(''); setEvents([]); setEventChoice(''); setSessionId('');
    if (!clientId) return;
    setKeysLoading(true);
    externalApiKeyApi.listForClient(clientId)
      .then(res => setKeys(res.data.data?.keys || []))
      .catch(() => toast.error('Failed to load API keys'))
      .finally(() => setKeysLoading(false));
    eventsApi.getAll({ clientId, limit: 200 })
      .then(res => setEvents(res.data.data || []))
      .catch(() => toast.error('Failed to load events'));
  }, [clientId]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [lines]);

  // Changing the key invalidates the loaded secret and the session
  useEffect(() => { setActiveKey(''); }, [keyId]);

  const loadKey = async () => {
    if (!selectedKey) return;
    setLoadingKey(true);
    try {
      const res = await externalApiKeyApi.reveal(clientId, selectedKey._id, selectedKey.currentVersion);
      if (res.data.success && res.data.data) {
        setActiveKey(res.data.data.key);
        toast.success(`Loaded ${selectedKey.name} (v${selectedKey.currentVersion}) — this reveal is audited`);
      }
    } catch (error: any) {
      toast.error(apiErrorMessage(error, 'Failed to load key'));
    } finally {
      setLoadingKey(false);
    }
  };

  const record = (result: ChatExternalCallResult, apiKey: string | undefined, body: unknown, raw = false) => {
    setLastCall({ result, curl: buildChatExternalCurl(apiKey, body, raw) });
  };

  const send = async () => {
    if (!message.trim() || sending) return;
    if (!effectiveKey) { toast.error('Load or paste an API key first'); return; }
    if (!eventId) { toast.error('Choose or enter an event'); return; }
    const body: Record<string, string> = { eventId, message: message.trim() };
    if (continueSession && sessionId) body.sessionId = sessionId;
    setLines(l => [...l, { id: `u${Date.now()}`, role: 'user', text: message.trim() }]);
    setMessage('');
    setSending(true);
    setVerify(null);
    const result = await callChatExternal({ apiKey: effectiveKey, body });
    record(result, effectiveKey, body);
    if (result.status === 200 && result.body?.success) {
      setSessionId(result.body.data.sessionId);
      setLines(l => [...l, { id: `b${Date.now()}`, role: 'bot', text: result.body.data.response }]);
    } else {
      setLines(l => [...l, { id: `e${Date.now()}`, role: 'error', text: `${result.status || 'ERR'} ${errorCodeOf(result)}: ${result.body?.error?.message || result.networkError || result.statusText}` }]);
    }
    setSending(false);
  };

  const newSession = () => { setSessionId(''); setLines([]); setVerify(null); toast.info('New session — the next message starts a fresh conversation'); };

  const addScenario = (r: ScenarioResult) => setScenarioResults(prev => [r, ...prev.filter(p => p.id !== r.id)]);

  /** Runs one scenario call and compares the error code with the contract in the spec. */
  const runCall = async (id: string, name: string, expectedStatus: number, expectedCode: string, apiKey: string | undefined, body: unknown, opts: { raw?: boolean } = {}) => {
    const result = await callChatExternal({ apiKey, body, raw: opts.raw });
    record(result, apiKey, body, opts.raw);
    const code = errorCodeOf(result);
    addScenario({ id, name, expected: `${expectedStatus} ${expectedCode}`, actual: `${result.status || 'ERR'} ${code}`, passed: result.status === expectedStatus && code === expectedCode });
    return result;
  };

  const needEvent = (): boolean => { if (!eventId) { toast.error('Choose or enter an event first'); return false; } return true; };
  const baseBody = () => ({ eventId, message: 'Scenario test: hello' });

  const scenarios: { id: string; name: string; expected: string; run: () => Promise<void>; needsKey?: boolean }[] = [
    { id: 'nokey', name: 'No key', expected: '401 MISSING_API_KEY', run: async () => { if (!needEvent()) return; await runCall('nokey', 'No key', 401, 'MISSING_API_KEY', undefined, baseBody()); } },
    {
      id: 'wrongkey', name: 'Wrong key', expected: '401 INVALID_API_KEY',
      run: async () => {
        if (!needEvent()) return;
        const prefix = selectedKey ? selectedKey.keyPrefix : `ebk_live_${randomHex(8)}`;
        await runCall('wrongkey', 'Wrong key', 401, 'INVALID_API_KEY', `${prefix}_${randomSecret()}`, baseBody());
      },
    },
    {
      id: 'revoked', name: 'Revoked version', expected: '401 API_KEY_REVOKED',
      run: async () => {
        if (!clientId) { toast.error('Pick a client first — a throwaway key is created, revoked and deleted for this scenario'); return; }
        if (!needEvent()) return;
        setRunningScenario('revoked');
        let tempKeyId = '';
        try {
          const created = await externalApiKeyApi.create({ clientId, name: `[tester] revoked-demo ${Date.now()}`, environment: 'test', settings: { rateLimitPerMinute: 5 } });
          const secret = created.data.data;
          if (!secret) throw new Error('Key creation failed');
          tempKeyId = secret.keyId;
          await externalApiKeyApi.revokeKey(clientId, tempKeyId, 'Tester scenario: revoked version');
          await runCall('revoked', 'Revoked version', 401, 'API_KEY_REVOKED', secret.key, baseBody());
        } catch (error: any) {
          toast.error(apiErrorMessage(error, 'Scenario failed'));
        } finally {
          if (tempKeyId) { try { await externalApiKeyApi.remove(clientId, tempKeyId); } catch { /* leave the revoked key for manual cleanup */ } }
          setRunningScenario('');
        }
      },
    },
    {
      id: 'foreignevent', name: 'Event the key cannot access', expected: '403 EVENT_NOT_ALLOWED',
      run: async () => { if (!effectiveKey) { toast.error('Load or paste a key first'); return; } await runCall('foreignevent', 'Event the key cannot access', 403, 'EVENT_NOT_ALLOWED', effectiveKey, { eventId: randomHex(24), message: 'Scenario test: hello' }); },
      needsKey: true,
    },
    {
      id: 'toolong', name: 'Over-long message', expected: '400 VALIDATION_ERROR',
      run: async () => { if (!effectiveKey) { toast.error('Load or paste a key first'); return; } if (!needEvent()) return; await runCall('toolong', 'Over-long message', 400, 'VALIDATION_ERROR', effectiveKey, { eventId, message: 'a'.repeat(20000) }); },
      needsKey: true,
    },
    {
      id: 'badsession', name: 'Unknown sessionId', expected: '404 SESSION_NOT_FOUND',
      run: async () => { if (!effectiveKey) { toast.error('Load or paste a key first'); return; } if (!needEvent()) return; await runCall('badsession', 'Unknown sessionId', 404, 'SESSION_NOT_FOUND', effectiveKey, { ...baseBody(), sessionId: crypto.randomUUID() }); },
      needsKey: true,
    },
    {
      id: 'rapid', name: 'Rapid-fire (rate limit)', expected: '429 RATE_LIMITED',
      run: async () => {
        if (!effectiveKey) { toast.error('Load or paste a key first'); return; }
        const limit = selectedKey?.settings.rateLimitPerMinute ?? 60;
        const count = limit + 5;
        if (count > 150) { toast.error(`This key allows ${limit}/min — too high to trigger from here. Lower its rate limit first.`); return; }
        setRunningScenario('rapid');
        // Bodies are deliberately invalid (no message): requests pass auth and count against the
        // per-key limiter, but are rejected by validation, so no AI call is made or billed.
        const results = await Promise.all(Array.from({ length: count }, () => callChatExternal({ apiKey: effectiveKey, body: { eventId: eventId || randomHex(24) } })));
        const limited = results.filter(r => r.status === 429);
        const sample = limited[0] || results[results.length - 1];
        record(sample, effectiveKey, { eventId: eventId || '<eventId>' });
        addScenario({
          id: 'rapid', name: 'Rapid-fire (rate limit)', expected: '429 RATE_LIMITED',
          actual: limited.length ? `429 RATE_LIMITED (${limited.length} of ${count} requests; Retry-After ${sample.headers['retry-after'] || '?'}s)` : `no 429 in ${count} requests`,
          passed: limited.length > 0 && errorCodeOf(limited[0]) === 'RATE_LIMITED',
          note: 'This key is now rate limited for up to a minute.',
        });
        setRunningScenario('');
      },
      needsKey: true,
    },
  ];

  const runScenario = async (s: typeof scenarios[number]) => {
    setRunningScenario(prev => prev || s.id);
    try { await s.run(); } finally { setRunningScenario(prev => (prev === s.id ? '' : prev)); }
  };

  const verifyStorage = async () => {
    if (!sessionId) return;
    setVerify({ loading: true, checks: [] });
    try {
      const checks: { label: string; ok: boolean; detail?: string }[] = [];
      const sessionRes = await sessionsApi.getById(sessionId);
      const session = sessionRes.data.data as Session | undefined;
      if (!session) throw new Error('Session not found');
      checks.push({ label: 'Session saved with source "api"', ok: session.source === 'api', detail: `source = ${session.source}` });
      checks.push({ label: 'Saved against the account owner', ok: !clientId || session.userId === clientId, detail: `userId = ${session.userId}` });
      checks.push({ label: 'Conversation recorded', ok: (session.conversations?.length || 0) > 0, detail: `${session.conversations?.length || 0} message(s)` });
      if (selectedKey && keySource === 'existing') {
        checks.push({ label: 'Session tagged with this key', ok: session.apiKeyId === selectedKey._id, detail: `apiKeyId = ${session.apiKeyId || 'none'}` });
        const usage = await externalApiKeyApi.getUsage(clientId, selectedKey._id, 1);
        const u = usage.data.data;
        checks.push({ label: 'Cost rows tagged with this key', ok: !!u && u.totals.aiCalls > 0, detail: u ? `${u.totals.aiCalls} AI call(s) today · ${u.totals.inputTokens.toLocaleString()} in / ${u.totals.outputTokens.toLocaleString()} out · ${formatUsd(u.totals.costUsd)} · versions ${u.byVersion.map(v => `v${v.versionNumber ?? '?'}`).join(', ') || '–'}` : '' });
      }
      setVerify({ loading: false, checks });
    } catch (error: any) {
      setVerify({ loading: false, checks: [], error: apiErrorMessage(error, 'Verification failed') });
    }
  };

  const lastResult = lastCall?.result;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          This tab calls the real <code className="text-xs bg-white/60 px-1 rounded">POST /api/chat/external</code> with an API key, the same way a third-party backend would.
          Real AI calls are made (and billed to the key owner), except in the rapid-fire scenario. The key is held in memory only and is never stored.
        </span>
      </div>

      {/* Key + event configuration */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <h3 className="text-base font-semibold text-gray-800">API key and event</h3>
        <div className="flex gap-4 text-sm">
          {(['existing', 'manual'] as const).map(m => (
            <label key={m} className="flex items-center gap-2 text-gray-700">
              <input type="radio" checked={keySource === m} onChange={() => setKeySource(m)} />
              {m === 'existing' ? 'Pick an existing key' : 'Paste a key manually'}
            </label>
          ))}
        </div>

        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Client</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-72">
              <option value="">Select a client</option>
              {clients.map(c => <option key={c._id} value={c._id}>{c.name} ({c.email})</option>)}
            </select>
          </div>

          {keySource === 'existing' ? (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">API key</label>
                <select value={keyId} onChange={e => setKeyId(e.target.value)} disabled={!clientId || keysLoading} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-72">
                  <option value="">{keysLoading ? 'Loading...' : keys.length ? 'Select a key' : clientId ? 'No keys for this client' : 'Select a client first'}</option>
                  {keys.map(k => <option key={k._id} value={k._id}>{k.name} · {k.status} · v{k.currentVersion}</option>)}
                </select>
              </div>
              <button onClick={loadKey} disabled={!selectedKey || loadingKey || selectedKey.status === 'revoked'} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                {loadingKey && <Loader2 className="w-4 h-4 animate-spin" />}
                {activeKey ? 'Key loaded' : 'Load key (audited reveal)'}
              </button>
            </>
          ) : (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">API key (e.g. a revoked or invalid one)</label>
              <div className="relative w-96">
                <input type={showKey ? 'text' : 'password'} value={manualKey} onChange={e => setManualKey(e.target.value)} autoComplete="off" spellCheck={false}
                  placeholder="ebk_live_..." className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <button type="button" onClick={() => setShowKey(s => !s)} aria-label={showKey ? 'Hide key' : 'Show key'} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Event</label>
            <select value={eventChoice} onChange={e => setEventChoice(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-72">
              <option value="">Select an event</option>
              {events.map(ev => <option key={ev._id} value={ev._id}>{ev.name}</option>)}
              <option value="__custom__">Enter an event id…</option>
            </select>
          </div>
          {eventChoice === '__custom__' && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Event id</label>
              <input value={customEventId} onChange={e => setCustomEventId(e.target.value)} placeholder="24-character id" className="px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 w-64" />
            </div>
          )}
        </div>
        {selectedKey && keySource === 'existing' && (
          <p className="text-xs text-gray-500">
            {selectedKey.allowedEventIds.length ? `Restricted to ${selectedKey.allowedEventIds.length} event(s).` : 'Can access all of this client\'s events.'} Rate limit {selectedKey.settings.rateLimitPerMinute}/min
            {selectedKey.settings.monthlyCostLimitUsd !== undefined ? ` · monthly cap ${formatUsd(selectedKey.settings.monthlyCostLimitUsd)}` : ''}.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Conversation */}
        <div className="bg-white border border-gray-200 rounded-lg flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-800">Conversation</h3>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-gray-600">
                <input type="checkbox" checked={continueSession} onChange={e => setContinueSession(e.target.checked)} className="rounded" />
                Continue session
              </label>
              <button onClick={newSession} className="px-3 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50">New session</button>
            </div>
          </div>
          {sessionId && <p className="px-4 pt-2 text-xs text-gray-400 font-mono truncate">sessionId: {sessionId}</p>}
          <div ref={chatRef} className="flex-1 min-h-[260px] max-h-[420px] overflow-y-auto p-4 space-y-3">
            {lines.length === 0 ? <p className="text-sm text-gray-400 text-center py-10">Send a message to call the API.</p> : lines.map(line => (
              <div key={line.id} className={`flex gap-2 ${line.role === 'user' ? 'justify-end' : ''}`}>
                {line.role !== 'user' && (line.role === 'bot' ? <Bot className="w-5 h-5 text-blue-600 mt-1 shrink-0" /> : <AlertTriangle className="w-5 h-5 text-red-500 mt-1 shrink-0" />)}
                <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${line.role === 'user' ? 'bg-blue-600 text-white' : line.role === 'bot' ? 'bg-gray-100 text-gray-800' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {line.role === 'bot' ? <div className="prose prose-sm max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]}>{line.text}</ReactMarkdown></div> : line.text}
                </div>
                {line.role === 'user' && <UserIcon className="w-5 h-5 text-gray-400 mt-1 shrink-0" />}
              </div>
            ))}
            {sending && <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Waiting for the API...</div>}
          </div>
          <div className="flex gap-2 p-3 border-t border-gray-200">
            <input value={message} onChange={e => setMessage(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type a message (try “start”)…" className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <button onClick={send} disabled={sending || !message.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
              <Send className="w-4 h-4" /> Send
            </button>
          </div>
          <div className="px-4 py-3 border-t border-gray-100 space-y-2">
            <button onClick={verifyStorage} disabled={!sessionId || verify?.loading} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">
              {verify?.loading ? 'Checking...' : 'Verify saved session and cost rows'}
            </button>
            {verify?.error && <p className="text-xs text-red-600">{verify.error}</p>}
            {verify && !verify.loading && verify.checks.length > 0 && (
              <ul className="space-y-1">
                {verify.checks.map(c => (
                  <li key={c.label} className="flex items-start gap-2 text-xs">
                    {c.ok ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
                    <span className="text-gray-700">{c.label}{c.detail ? <span className="text-gray-400"> — {c.detail}</span> : null}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Inspector */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200"><h3 className="text-base font-semibold text-gray-800">Request / response inspector</h3></div>
          {!lastCall || !lastResult ? <p className="text-sm text-gray-400 text-center py-10">The last request and response appear here.</p> : (
            <div className="p-4 space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Request (key masked)</span>
                  <button onClick={() => { navigator.clipboard.writeText(lastCall.curl).then(() => toast.success('curl copied')).catch(() => toast.error('Copy failed')); }} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"><Copy className="w-3 h-3" /> Copy curl</button>
                </div>
                <pre className="px-3 py-2 bg-gray-900 text-gray-100 rounded-md text-xs overflow-x-auto font-mono whitespace-pre-wrap break-all">{lastCall.curl}</pre>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${lastResult.status >= 200 && lastResult.status < 300 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                  {lastResult.status || 'ERR'} {lastResult.statusText}
                </span>
                <span className="text-gray-500">{lastResult.latencyMs} ms round trip</span>
              </div>
              {lastResult.networkError && <p className="text-xs text-red-600">{lastResult.networkError}</p>}
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Response headers</span>
                <pre className="mt-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-xs font-mono overflow-x-auto">{Object.entries(lastResult.headers).map(([k, v]) => `${k}: ${v}`).join('\n') || '(none exposed)'}</pre>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Response body</span>
                <pre className="mt-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-xs font-mono overflow-auto max-h-72">{typeof lastResult.body === 'string' ? lastResult.body : JSON.stringify(lastResult.body, null, 2)}</pre>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scenarios */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-base font-semibold text-gray-800 mb-1">Scenario shortcuts</h3>
        <p className="text-xs text-gray-500 mb-3">Each button runs one call and checks the answer against the documented error contract. The key and event above are used where needed.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {scenarios.map(s => (
            <button key={s.id} onClick={() => runScenario(s)} disabled={!!runningScenario} title={`Expected: ${s.expected}`}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
              {runningScenario === s.id && <Loader2 className="w-3 h-3 animate-spin" />}
              {s.name}
              <span className="text-xs text-gray-400">{s.expected}</span>
            </button>
          ))}
        </div>
        {scenarioResults.length > 0 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200"><tr>{['Scenario', 'Expected', 'Actual', 'Result'].map(h => <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-100">
                {scenarioResults.map(r => (
                  <tr key={r.id}>
                    <td className="px-3 py-2 text-gray-900">{r.name}{r.note && <div className="text-xs text-gray-400">{r.note}</div>}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-600">{r.expected}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-600">{r.actual}</td>
                    <td className="px-3 py-2">{r.passed ? <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium"><CheckCircle2 className="w-4 h-4" /> As expected</span> : <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium"><XCircle className="w-4 h-4" /> Unexpected</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const ChatTest: React.FC = () => {
  const { isAdmin, user } = useAuth();
  const [mode, setMode] = useState<'chat' | 'external'>('chat');
  const [events, setEvents] = useState<Event[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [clients, setClients] = useState<AppUser[]>([]);
  const [loadingClients, setLoadingClients] = useState<boolean>(false);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [currentSession, setCurrentSession] = useState<Session | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [inspectionVisible, setInspectionVisible] = useState(false);
  const [lastPromptData, setLastPromptData] = useState<any>(null);
  const [conversationStarted, setConversationStarted] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const lastFetchedAtRef = useRef<string | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // RAG Chat state - Default to TRUE for better answers
  const [useRAG, setUseRAG] = useState(true);
  const [ragTopK, setRagTopK] = useState(5);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isAdmin && clients.length === 0) {
      fetchClients();
    }
    fetchEvents();
    fetchUsers();
  }, [isAdmin]);

  // Fetch events when client filter changes (for admins)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isAdmin) {
      fetchEvents();
      // Reset event selection when client changes
      if (selectedEvent) {
        setSelectedEvent('');
        setMessages([]);
        setCurrentSession(null);
        setLastPromptData(null);
        setConversationStarted(false);
      }
    }
  }, [selectedClientId]);

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const response = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      if (response.data.success && response.data.data) {
        const users = response.data.data.users || [];
        setClients(users);
      }
    } catch (error: any) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  // Auto-enable conversation when RAG mode is enabled and event is selected
  useEffect(() => {
    if (useRAG && selectedEvent && messages.length === 0) {
      setConversationStarted(true);

      // Add welcome message for RAG mode
      const welcomeMessage: ChatMessage = {
        id: 'rag-welcome',
        type: 'bot',
        content: `👋 **Welcome to RAG Mode!**

I'll search through the document chunks and answer your questions based on the most relevant content.

🔍 **How it works:**
- I retrieve the top ${ragTopK} most relevant chunks from your documents
- Only the relevant parts are sent to the AI (not full documents)
- This makes responses faster and more focused

💡 **Try asking:**
- "What is this project about?"
- "Explain the main features"
- "What problem does it solve?"

Go ahead, ask me anything about your documents!`,
        timestamp: new Date(),
      };

      setMessages([welcomeMessage]);
    }
  }, [useRAG, selectedEvent, ragTopK]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchEvents = async () => {
    try {
      const params: any = { isActive: true, limit: 100 };

      // For admins, filter events by selected client
      if (isAdmin && selectedClientId) {
        params.clientId = selectedClientId;
      } else if (!isAdmin && user?._id) {
        // Clients automatically see only their events
        params.clientId = user._id;
      }
      // If admin and no client selected, show all events

      const response = await eventsApi.getAll(params);
      setEvents(response.data.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiService.get('/users');
      if (response.success && response.data) {
        setUsers(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleEventChange = async (eventId: string) => {
    setSelectedEvent(eventId);
    setMessages([]);
    setCurrentSession(null);
    setLastPromptData(null);
    setConversationStarted(false);
    // notifications reset and initial fetch
    setNotifications([]);
    setUnreadCount(0);
    lastFetchedAtRef.current = null;
    if (pollingRef.current) clearInterval(pollingRef.current as any);
    if (eventId) {
      await fetchNotifications(eventId);
      startNotificationPolling(eventId);
    }
  };

  const handleUserChange = (userId: string) => {
    setSelectedUser(userId);
    setMessages([]);
    setCurrentSession(null);
    setLastPromptData(null);
    setConversationStarted(false);
  };

  const startNotificationPolling = (eventId: string) => {
    if (pollingRef.current) clearInterval(pollingRef.current as any);
    pollingRef.current = setInterval(() => {
      fetchNotifications(eventId);
    }, 30000);
  };

  const fetchNotifications = async (eventId: string) => {
    try {
      const since = lastFetchedAtRef.current || undefined;
      const resp = await notificationsApi.listForEvent(eventId, { since });
      const list = resp.data.data?.notifications || [];
      if (list.length) {
        setNotifications(prev => {
          const existingIds = new Set(prev.map(n => n._id));
          const newOnes = list.filter(n => !existingIds.has(n._id));
          const merged = [...newOnes, ...prev];
          if (newOnes.length) setUnreadCount(count => count + newOnes.length);
          return merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        });
      }
      lastFetchedAtRef.current = new Date().toISOString();
    } catch (e) {
      console.error('Failed to fetch notifications', e);
    }
  };

  const openNotifications = async () => {
    setShowNotifications(true);
    try {
      const unseen = notifications;
      await Promise.all(unseen.map(n => notificationsApi.markSeen(n._id, { userId: selectedUser || undefined } as any).catch(() => null)));
      setUnreadCount(0);
    } catch (e) {
      // ignore
    }
  };

  const startConversation = async () => {
    if (!selectedEvent || !selectedUser) return;

    setLoading(true);
    try {
      const selectedUserData = users.find(u => u.userId === selectedUser);
      if (!selectedUserData) {
        console.error('Selected user not found');
        return;
      }

      // Send "start" message to initialize the session
      const chatRequest = {
        userId: selectedUser,
        eventId: selectedEvent,
        message: 'start',
        sessionId: undefined, // Let backend create new session
      };

      const response = await chatApi.sendMessage(chatRequest);

      if (response.data.success && response.data.data) {
        const chatData = response.data.data;

        const welcomeMessage: ChatMessage = {
          id: Date.now().toString(),
          type: 'bot',
          content: chatData.response,
          timestamp: new Date(),
          metadata: chatData.metadata,
        };

        setMessages([welcomeMessage]);

        // Create a session object from the response
        if (chatData.sessionId) {
          const sessionData: Session = {
            sessionId: chatData.sessionId,
            userId: selectedUser,
            eventId: selectedEvent,
            userData: users.find(u => u.userId === selectedUser)!,
            documentData: [],
            guardrails: [],
            behavioralPrompts: [],
            conversations: [],
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          setCurrentSession(sessionData);

          // Fetch real event data for prompt inspection
          fetchEventDataForPromptInspection('start', chatData.response, chatData.metadata);
        }
        setConversationStarted(true);
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !selectedEvent || !selectedUser) return;

    // Use RAG chat if enabled
    if (useRAG) {
      await handleSendRAGMessage();
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const chatRequest = {
        userId: selectedUser,
        eventId: selectedEvent,
        message: inputMessage,
        sessionId: currentSession?.sessionId,
      };

      const response = await chatApi.sendMessage(chatRequest);
      const chatResponse = response.data.data;

      if (chatResponse) {
        // Get updated session data for prompt inspection
        const sessionResponse = await sessionsApi.getById(chatResponse.sessionId);
        const sessionData = sessionResponse.data.data;

        setCurrentSession(sessionData || null);

        // Fetch real event data for prompt inspection
        fetchEventDataForPromptInspection(inputMessage, chatResponse.response, response.data.data?.metadata || {});

        const botMessage: ChatMessage = {
          id: chatResponse.conversationId,
          type: 'bot',
          content: chatResponse.response,
          timestamp: new Date(),
          metadata: chatResponse.metadata,
        };

        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'bot',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRAGMessage = async () => {
    if (!inputMessage.trim() || !selectedEvent) {
      console.log('Missing required fields for RAG:', { inputMessage: !!inputMessage.trim(), selectedEvent: !!selectedEvent });
      return;
    }

    console.log('🚀 Sending RAG message:', { query: inputMessage, eventId: selectedEvent, topK: ragTopK });

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    const queryText = inputMessage;
    setInputMessage('');
    setLoading(true);

    try {
      const ragRequest = {
        query: queryText,
        eventId: selectedEvent,
        topK: ragTopK,
        userId: selectedUser,
        sessionId: currentSession?.sessionId,
      };

      console.log('📤 RAG Request:', ragRequest);
      const response = await apiService.post('/chat/rag', ragRequest);
      console.log('📥 RAG Response:', response);

      if (response.success && response.data) {
        const ragData = response.data;

        // Store RAG-specific data for inspection
        setLastPromptData({
          userMessage: queryText,
          sessionData: null, // RAG doesn't use session
          response: ragData.response,
          metadata: {
            chunksUsed: ragData.chunksUsed,
            promptSent: ragData.promptSent,
            model: ragData.model,
            tokensUsed: ragData.tokensUsed,
            processingTime: ragData.processingTime,
          },
          isRAG: true,
        });

        const botMessage: ChatMessage = {
          id: Date.now().toString(),
          type: 'bot',
          content: ragData.response,
          timestamp: new Date(),
          metadata: {
            chunksUsed: ragData.chunksUsed,
            tokensUsed: ragData.tokensUsed,
            processingTime: ragData.processingTime,
          },
        };

        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error: any) {
      console.error('Error sending RAG message:', error);
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'bot',
        content: `Sorry, I encountered an error with RAG chat: ${error.response?.data?.message || error.message || 'Unknown error'}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const fetchEventDataForPromptInspection = async (userMessage: string, response: string, metadata: any) => {
    try {
      if (!selectedEvent) return;

      // Fetch guardrails, behavioral prompts, and documents for the selected event
      const [guardrailsResponse, promptsResponse, documentsResponse] = await Promise.all([
        apiService.get(`/guardrails/event/${selectedEvent}`),
        apiService.get(`/behavioral-prompts/event/${selectedEvent}`),
        apiService.get(`/documents/event/${selectedEvent}`)
      ]);

      const sessionData: Session = {
        sessionId: currentSession?.sessionId || 'temp',
        userId: selectedUser,
        eventId: selectedEvent,
        userData: users.find(u => u.userId === selectedUser)!,
        documentData: documentsResponse.success ? documentsResponse.data || [] : [],
        guardrails: guardrailsResponse.success ? guardrailsResponse.data || [] : [],
        behavioralPrompts: promptsResponse.success ? promptsResponse.data || [] : [],
        conversations: currentSession?.conversations || [],
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      setLastPromptData({
        userMessage,
        sessionData,
        response,
        metadata
      });
    } catch (error) {
      console.error('Error fetching event data for prompt inspection:', error);
      // Fallback with empty data
      setLastPromptData({
        userMessage,
        sessionData: null,
        response,
        metadata
      });
    }
  };

  const clearChat = () => {
    setMessages([]);
    setCurrentSession(null);
    setLastPromptData(null);
  };

  const renderPromptInspection = () => {
    if (!lastPromptData) return null;

    const { sessionData, userMessage, response, metadata, isRAG } = lastPromptData;

    // RAG mode inspection
    if (isRAG) {
      return (
        <TabGroup
          defaultTab="prompt"
          tabs={[
            {
              key: 'prompt',
              label: 'RAG Prompt Sent',
              content: (
                <div>
                  <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md mb-4 text-sm text-blue-800">
                    <Info className="w-4 h-4 mt-0.5 shrink-0" />
                    This is the actual prompt sent to the AI with retrieved document chunks
                  </div>
                  <SyntaxHighlighter
                    language="text"
                    style={tomorrow}
                    customStyle={{ fontSize: '12px', maxHeight: '500px' }}
                  >
                    {metadata.promptSent || 'Prompt not available'}
                  </SyntaxHighlighter>
                </div>
              ),
            },
            {
              key: 'chunks',
              label: `Chunks Used (${metadata.chunksUsed?.length || 0})`,
              content: (
                <div>
                  <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md mb-4 text-sm text-green-800">
                    <Info className="w-4 h-4 mt-0.5 shrink-0" />
                    Retrieved {metadata.chunksUsed?.length || 0} most relevant chunks from documents
                  </div>
                  {metadata.chunksUsed && metadata.chunksUsed.length > 0 ? (
                    <div>
                      {metadata.chunksUsed.map((chunk: any, idx: number) => (
                        <CollapsePanel
                          key={idx}
                          header={
                            <span className="flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              <span className="font-medium">Chunk {idx + 1}</span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                Similarity: {(chunk.similarity * 100).toFixed(2)}%
                              </span>
                            </span>
                          }
                        >
                          <table className="w-full border border-gray-200 rounded text-sm mb-3">
                            <tbody>
                              <tr className="border-b border-gray-200">
                                <td className="px-3 py-2 font-medium bg-gray-50 w-36">Document ID</td>
                                <td className="px-3 py-2">{chunk.documentId}</td>
                              </tr>
                              <tr className="border-b border-gray-200">
                                <td className="px-3 py-2 font-medium bg-gray-50">Chunk Index</td>
                                <td className="px-3 py-2">{chunk.chunkIndex}</td>
                              </tr>
                              <tr>
                                <td className="px-3 py-2 font-medium bg-gray-50">Similarity Score</td>
                                <td className="px-3 py-2">{chunk.similarity?.toFixed(4)}</td>
                              </tr>
                            </tbody>
                          </table>
                          <p className="text-sm font-medium mb-1">Content:</p>
                          <div className="bg-gray-100 p-3 rounded text-xs font-mono whitespace-pre-wrap max-h-72 overflow-y-auto">
                            {chunk.content}
                          </div>
                        </CollapsePanel>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
                      <Info className="w-4 h-4 mt-0.5 shrink-0" />
                      No chunks were used (no relevant documents found)
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: 'metadata',
              label: 'Response Metadata',
              content: (
                <div>
                  <table className="w-full border border-gray-200 rounded text-sm mb-4">
                    <tbody>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 font-medium bg-gray-50 w-40">Model</td>
                        <td className="px-3 py-2">{metadata.model}</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 font-medium bg-gray-50">Tokens Used</td>
                        <td className="px-3 py-2">{metadata.tokensUsed || 'N/A'}</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 font-medium bg-gray-50">Processing Time</td>
                        <td className="px-3 py-2">{metadata.processingTime}ms</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-medium bg-gray-50">Chunks Retrieved</td>
                        <td className="px-3 py-2">{metadata.chunksUsed?.length || 0}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="text-sm font-medium mb-2">Full Response:</p>
                  <SyntaxHighlighter language="json" style={tomorrow} customStyle={{ fontSize: '12px', marginTop: '8px' }}>
                    {JSON.stringify(response, null, 2)}
                  </SyntaxHighlighter>
                </div>
              ),
            },
          ]}
        />
      );
    }

    // Regular chat mode inspection
    const constructedPrompt = buildPromptString(sessionData || null, userMessage || 'No message available');

    return (
      <TabGroup
        defaultTab="prompt"
        tabs={[
          {
            key: 'prompt',
            label: 'Generated Prompt',
            content: (
              <SyntaxHighlighter
                language="text"
                style={tomorrow}
                customStyle={{ fontSize: '12px', maxHeight: '400px' }}
              >
                {constructedPrompt}
              </SyntaxHighlighter>
            ),
          },
          {
            key: 'session',
            label: 'Session Data',
            content: (
              <div>
                <CollapsePanel header="User Data">
                  <SyntaxHighlighter language="json" style={tomorrow} customStyle={{ fontSize: '12px' }}>
                    {JSON.stringify(sessionData.userData, null, 2)}
                  </SyntaxHighlighter>
                </CollapsePanel>
                <CollapsePanel header={`Guardrails (${sessionData.guardrails.length})`}>
                  <SyntaxHighlighter language="json" style={tomorrow} customStyle={{ fontSize: '12px' }}>
                    {JSON.stringify(sessionData.guardrails, null, 2)}
                  </SyntaxHighlighter>
                </CollapsePanel>
                <CollapsePanel header={`Behavioral Prompts (${sessionData.behavioralPrompts.length})`}>
                  <SyntaxHighlighter language="json" style={tomorrow} customStyle={{ fontSize: '12px' }}>
                    {JSON.stringify(sessionData.behavioralPrompts, null, 2)}
                  </SyntaxHighlighter>
                </CollapsePanel>
                <CollapsePanel header={`Documents (${sessionData.documentData.length})`}>
                  <SyntaxHighlighter language="json" style={tomorrow} customStyle={{ fontSize: '12px' }}>
                    {JSON.stringify(sessionData.documentData.map((doc: any) => ({
                      fileName: doc.originalName,
                      fileType: doc.fileType,
                      summary: doc.summary,
                      keywords: doc.keywords,
                      textLength: doc.extractedText.length,
                    })), null, 2)}
                  </SyntaxHighlighter>
                </CollapsePanel>
                <CollapsePanel header={`Conversation History (${sessionData.conversations.length})`}>
                  <SyntaxHighlighter language="json" style={tomorrow} customStyle={{ fontSize: '12px' }}>
                    {JSON.stringify(sessionData.conversations, null, 2)}
                  </SyntaxHighlighter>
                </CollapsePanel>
              </div>
            ),
          },
          {
            key: 'metadata',
            label: 'Response Metadata',
            content: (
              <SyntaxHighlighter language="json" style={tomorrow} customStyle={{ fontSize: '12px' }}>
                {JSON.stringify(response, null, 2)}
              </SyntaxHighlighter>
            ),
          },
        ]}
      />
    );
  };

  const buildPromptString = (sessionData: Session | null, userMessage: string) => {
    if (!sessionData) {
      return `[Session data not available for prompt reconstruction]

User Message: "${userMessage}"

Note: This is a simulated prompt reconstruction. The actual backend uses real session data including:
- Guardrails for content validation
- Behavioral prompts for response style
- Document context from uploaded files
- Conversation history for context`;
    }

    const { guardrails, behavioralPrompts, documentData, conversations } = sessionData;
    const detectedLanguage = 'auto-detected'; // This would be the actual detected language

    const relevantPrompts = behavioralPrompts
      .filter(p => p.isActive)
      .sort((a, b) => b.priority - a.priority);

    const documentContext = documentData
      .filter(d => d.isProcessed)
      .map(d => `Document: ${d.originalName}\nContent: ${d.extractedText.substring(0, 2000)}...`)
      .join('\n\n');

    const conversationHistory = conversations
      .slice(-10)
      .map(c => `User: ${c.userMessage}\nBot: ${c.botResponse}`)
      .join('\n\n');

    const activeGuardrails = guardrails
      .filter(g => g.isActive)
      .sort((a, b) => b.priority - a.priority)
      .map(g => g.rules)
      .flat();

    return `You are a helpful, friendly, and knowledgeable chatbot for a course event. You must respond in ${detectedLanguage} language.

BEHAVIORAL GUIDELINES:
${relevantPrompts.map(p => `- ${p.prompt}`).join('\n')}

GUARDRAILS (MUST FOLLOW):
${activeGuardrails.map((rule, index) => `${index + 1}. ${rule}`).join('\n')}

DOCUMENT CONTEXT:
${documentContext}

CONVERSATION HISTORY:
${conversationHistory}

Current user message: "${userMessage}"

Instructions:
1. Respond only to topics related to the course documents provided
2. Be friendly, helpful, and engaging
3. If the question is not related to the course content, politely redirect to course-related topics
4. Use the conversation history for context but focus on the current question
5. Respond in ${detectedLanguage} language
6. Follow all guardrails strictly
7. Keep responses concise but informative

Response:`;
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Bot className="w-7 h-7" />
          Chatbot Testing Interface
        </h2>
        <p className="text-gray-500 mt-1">
          Test the chatbot functionality with real session data and inspect the AI prompts
        </p>
      </div>

      {isAdmin && (
        <div className="flex gap-1 border-b border-gray-200 mb-4" role="tablist">
          {([['chat', 'Chat Test'], ['external', 'External API']] as const).map(([id, label]) => (
            <button key={id} role="tab" aria-selected={mode === id} onClick={() => setMode(id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${mode === id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {isAdmin && mode === 'external' ? <ExternalApiTester /> : (
      <>
      {/* Configuration */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h3 className="text-base font-semibold text-gray-800 mb-4">Test Configuration</h3>
        <div className="flex flex-wrap gap-4 items-end">
          {isAdmin && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Client</label>
              <select
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-72"
                value={selectedClientId}
                disabled={loadingClients}
                onChange={e => {
                  setSelectedClientId(e.target.value);
                  setSelectedEvent('');
                  setMessages([]);
                  setCurrentSession(null);
                  setLastPromptData(null);
                  setConversationStarted(false);
                }}
              >
                <option value="">All Clients</option>
                {clients.map(client => (
                  <option key={client._id} value={client._id}>
                    {client.name} ({client.email})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Knowledge Assistant</label>
            <select
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-72"
              value={selectedEvent}
              onChange={e => handleEventChange(e.target.value)}
            >
              <option value="">Select a Knowledge Assistant to test</option>
              {events.map(event => (
                <option key={event._id} value={event._id}>
                  {event.name} ({event.eventId})
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">User</label>
            <select
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-72"
              value={selectedUser}
              onChange={e => handleUserChange(e.target.value)}
            >
              <option value="">Select a user to test</option>
              {users.map(u => (
                <option key={u.userId} value={u.userId}>
                  {u.name} ({u.email}){u.occupation ? ` - ${u.occupation}` : ''}
                </option>
              ))}
            </select>
          </div>

          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={startConversation}
            disabled={!selectedEvent || !selectedUser || conversationStarted || loading || useRAG}
          >
            {loading && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {conversationStarted ? 'Conversation Started' : 'Start Conversation'}
          </button>

          <button
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={clearChat}
            disabled={!messages.length}
          >
            Clear Chat
          </button>

          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={() => setInspectionVisible(true)}
            disabled={!lastPromptData}
          >
            <Info className="w-4 h-4" />
            Inspect Last Prompt
          </button>
        </div>

        <hr className="border-gray-200 my-4" />

        {/* RAG Mode Configuration */}
        <div
          className={`flex items-start gap-3 p-3 rounded-md border mb-4 ${
            useRAG
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}
        >
          <Zap className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold">RAG Mode (Retrieval Augmented Generation)</p>
            <p className="text-sm mt-0.5">
              {useRAG
                ? 'RAG mode enabled: Only relevant document chunks will be sent to AI (no session, guardrails, or behavioral prompts)'
                : 'Regular chat mode: Uses full documents, session history, guardrails, and behavioral prompts'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Enable RAG Mode:</span>
            <button
              role="switch"
              aria-checked={useRAG}
              onClick={() => {
                setUseRAG(!useRAG);
                setMessages([]);
                setConversationStarted(false);
                setLastPromptData(null);
                if (useRAG) {
                  setCurrentSession(null);
                }
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                useRAG ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  useRAG ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                useRAG ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
              }`}
            >
              {useRAG ? 'RAG Active' : 'Regular Chat'}
            </span>
          </div>

          {useRAG && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Top K Chunks:</span>
              <input
                type="number"
                min={1}
                max={20}
                value={ragTopK}
                onChange={e => setRagTopK(Number(e.target.value) || 5)}
                className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-500">(Number of chunks to retrieve)</span>
            </div>
          )}
        </div>
      </div>

      {/* Chat Interface */}
      <div className="flex gap-4" style={{ height: '600px' }}>
        {/* Chat Messages */}
        <div className="flex-1 flex flex-col bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* Chat header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50">
            <MessageSquare className="w-4 h-4 text-gray-600" />
            <span className="font-medium text-gray-800">Chat Session</span>
            <div className="relative">
              <button
                onClick={openNotifications}
                className="p-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-100 text-sm flex items-center gap-1"
              >
                <Bell className="w-3.5 h-3.5" />
              </button>
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-xs font-bold">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            {currentSession && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                Session: {currentSession.sessionId.substring(0, 8)}...
              </span>
            )}
          </div>

          {/* Alerts */}
          {((!selectedEvent || (!selectedUser && !useRAG))) && (
            <div className="flex items-start gap-2 m-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              {!selectedEvent && !selectedUser
                ? useRAG
                  ? 'Please select a Knowledge Assistant to start RAG testing'
                  : 'Please select both a Knowledge Assistant and a user to start testing'
                : !selectedEvent
                  ? 'Please select a Knowledge Assistant to start testing'
                  : !useRAG
                    ? 'Please select a user to start testing'
                    : ''}
            </div>
          )}

          {(!conversationStarted && selectedEvent && selectedUser && !useRAG) && (
            <div className="flex items-center justify-between m-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 shrink-0" />
                Click 'Start Conversation' to begin the chat session
              </div>
              <button
                className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 flex items-center gap-1"
                onClick={startConversation}
                disabled={loading}
              >
                {loading && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Start Conversation
              </button>
            </div>
          )}

          {(selectedEvent && (useRAG || (selectedUser && conversationStarted))) && (
            <>
              {/* Messages Container */}
              <div
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto p-4 bg-gray-50"
              >
                {messages.length === 0 && (
                  <div className="flex flex-col items-center justify-center mt-16 text-gray-400">
                    <Bot className="w-12 h-12 mb-4" />
                    <p>Start a conversation to test the chatbot</p>
                  </div>
                )}

                <div className="space-y-2">
                  {messages.map(message => (
                    <div
                      key={message.id}
                      className={`flex w-full ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`flex gap-2 items-start max-w-[70%] ${
                          message.type === 'user' ? 'flex-row-reverse' : 'flex-row'
                        }`}
                      >
                        {message.type === 'bot' && (
                          <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                            <Bot className="w-4 h-4 text-white" />
                          </div>
                        )}

                        <div
                          className={`px-4 py-3 rounded-xl text-sm ${
                            message.type === 'user'
                              ? 'bg-blue-500 text-white whitespace-pre-wrap'
                              : 'bg-white text-gray-900 border border-gray-100'
                          }`}
                        >
                          {message.type === 'bot' ? (
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                h1: ({children}) => <h1 className="text-lg font-bold mb-2 text-blue-600">{children}</h1>,
                                h2: ({children}) => <h2 className="text-base font-bold mb-1.5 text-blue-600">{children}</h2>,
                                h3: ({children}) => <h3 className="text-sm font-bold mb-1 text-blue-600">{children}</h3>,
                                p: ({children}) => <p className="mb-2 leading-relaxed">{children}</p>,
                                ul: ({children}) => <ul className="mb-2 pl-5 list-disc">{children}</ul>,
                                ol: ({children}) => <ol className="mb-2 pl-5 list-decimal">{children}</ol>,
                                li: ({children}) => <li className="mb-1">{children}</li>,
                                strong: ({children}) => <strong className="font-bold text-blue-600">{children}</strong>,
                                em: ({children}) => <em className="italic text-purple-600">{children}</em>,
                                code: ({children}) => <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
                                blockquote: ({children}) => <blockquote className="border-l-4 border-blue-400 pl-3 my-2 italic">{children}</blockquote>,
                                table: ({children}) => <table className="w-full border-collapse mb-2">{children}</table>,
                                th: ({children}) => <th className="border border-gray-300 px-2 py-1 bg-gray-50 font-bold text-left">{children}</th>,
                                td: ({children}) => <td className="border border-gray-300 px-2 py-1">{children}</td>,
                              }}
                            >
                              {message.content}
                            </ReactMarkdown>
                          ) : (
                            message.content
                          )}
                        </div>

                        {message.type === 'user' && (
                          <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                            <UserIcon className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {loading && (
                  <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-500">
                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    Assistant is typing...
                  </div>
                )}
              </div>

              {/* Input Area */}
              <div className="p-4 border-t border-gray-200 flex gap-2">
                <textarea
                  rows={2}
                  value={inputMessage}
                  onChange={e => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    useRAG
                      ? 'Ask a question about the documents...'
                      : 'Type your message here... (Press Enter to send, Shift+Enter for new line)'
                  }
                  disabled={!selectedEvent || (!useRAG && (!selectedUser || !conversationStarted)) || loading}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || !selectedEvent || (!useRAG && (!selectedUser || !conversationStarted)) || loading}
                  className="px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
                  style={{ height: '60px' }}
                >
                  <Send className="w-4 h-4" />
                  Send
                </button>
              </div>
            </>
          )}
        </div>

        {/* Session Info Panel (only in regular mode) */}
        {currentSession && !useRAG && (
          <div className="w-96 bg-white border border-gray-200 rounded-lg p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-800">Session Information</h3>
              <button
                onClick={() => setInspectionVisible(true)}
                className="flex items-center gap-1 px-2 py-1 border border-gray-300 rounded text-xs text-gray-600 hover:bg-gray-50"
              >
                <Eye className="w-3.5 h-3.5" />
                Inspect
              </button>
            </div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="py-2 font-medium text-gray-600 w-36">Session ID</td>
                  <td className="py-2 text-gray-900"><code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{currentSession.sessionId.substring(0, 16)}...</code></td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">User ID</td>
                  <td className="py-2 text-gray-900">{currentSession.userId}</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Knowledge Assistant</td>
                  <td className="py-2 text-gray-900">{events.find(e => e._id === selectedEvent)?.name}</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Conversations</td>
                  <td className="py-2 text-gray-900">{currentSession.conversations.length}</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Documents</td>
                  <td className="py-2 text-gray-900">{currentSession.documentData.length}</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Guardrails</td>
                  <td className="py-2 text-gray-900">{currentSession.guardrails.filter(g => g.isActive).length} active</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Behavioral Prompts</td>
                  <td className="py-2 text-gray-900">{currentSession.behavioralPrompts.filter(p => p.isActive).length} active</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Created</td>
                  <td className="py-2 text-gray-900">{new Date(currentSession.createdAt).toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* RAG Info Panel (only in RAG mode) */}
        {useRAG && selectedEvent && (
          <div className="w-96 bg-white border border-gray-200 rounded-lg p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-500" />
                RAG Mode Active
              </h3>
              <button
                onClick={() => setInspectionVisible(true)}
                disabled={!lastPromptData}
                className="flex items-center gap-1 px-2 py-1 border border-gray-300 rounded text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Eye className="w-3.5 h-3.5" />
                Inspect
              </button>
            </div>
            <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md mb-4">
              <Info className="w-4 h-4 text-green-700 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-800">Vector Search Enabled</p>
                <p className="text-xs text-green-700 mt-0.5">Searching document chunks by semantic similarity</p>
              </div>
            </div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="py-2 font-medium text-gray-600 w-36">Mode</td>
                  <td className="py-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      RAG (Retrieval Augmented Generation)
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Knowledge Assistant</td>
                  <td className="py-2 text-gray-900">{events.find(e => e._id === selectedEvent)?.name}</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Top K Chunks</td>
                  <td className="py-2 text-gray-900">{ragTopK}</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-gray-600">Messages</td>
                  <td className="py-2 text-gray-900">{messages.length}</td>
                </tr>
                {lastPromptData?.metadata && (
                  <>
                    <tr>
                      <td className="py-2 font-medium text-gray-600">Last Response</td>
                      <td className="py-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          {lastPromptData.metadata.chunksUsed?.length || 0} chunks used
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 font-medium text-gray-600">Processing Time</td>
                      <td className="py-2 text-gray-900">{lastPromptData.metadata.processingTime}ms</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-medium text-gray-600">Tokens Used</td>
                      <td className="py-2 text-gray-900">{lastPromptData.metadata.tokensUsed || 'N/A'}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
            <p className="mt-4 text-xs text-gray-500">
              RAG mode retrieves only relevant chunks, not full documents
            </p>
          </div>
        )}
      </div>

      {/* Prompt Inspection Modal */}
      <Modal
        open={inspectionVisible}
        onClose={() => setInspectionVisible(false)}
        title={
          <>
            <Info className="w-4 h-4" />
            Prompt &amp; Session Data Inspection
          </>
        }
        wide
      >
        {renderPromptInspection()}
      </Modal>

      {/* Notifications Modal */}
      <Modal
        open={showNotifications}
        onClose={() => setShowNotifications(false)}
        title={
          <>
            <Bell className="w-4 h-4" />
            Knowledge Assistant Notifications
          </>
        }
      >
        {selectedEvent ? (
          <div className="divide-y divide-gray-100">
            {notifications.length === 0 ? (
              <p className="py-4 text-sm text-gray-500 text-center">No notifications yet</p>
            ) : (
              notifications.map(n => (
                <div key={n._id} className="py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        n.priority === 'critical'
                          ? 'bg-red-100 text-red-700'
                          : n.priority === 'warning'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {n.priority.toUpperCase()}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{n.title}</span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{n.body}</p>
                  <p className="text-xs text-gray-400 mt-1">{new Date(n.createdAt).toLocaleString()}</p>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            Select a Knowledge Assistant to view notifications
          </div>
        )}
      </Modal>
      </>
      )}
    </div>
  );
};

export default ChatTest;
